import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AuthModal() {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const isTeam = useLocation().pathname.startsWith("/admin");
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!authOpen) return;
    if (isTeam) setMode("login");
    const previous = document.activeElement;
    dialogRef.current?.querySelector("input")?.focus();
    const keyboard = (event) => {
      if (event.key === "Escape") setAuthOpen(false);
      if (event.key === "Tab") {
        const items = [...(dialogRef.current?.querySelectorAll("button:not(:disabled), input:not(:disabled), a[href]") || [])];
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", keyboard);
    return () => { document.removeEventListener("keydown", keyboard); previous?.focus(); };
  }, [authOpen, isTeam, setAuthOpen]);

  if (!authOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bienvenue !");
        setAuthOpen(false);
      } else if (mode === "register") {
        await register(form);
        toast.success("Compte créé !");
        setAuthOpen(false);
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Si ce compte existe, un email de réinitialisation a été envoyé.");
        setMode("login");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const title = mode === "login" ? "Connexion" : mode === "register" ? "Créer un compte" : "Mot de passe oublié";
  const subtitle = mode === "login"
    ? (isTeam ? "Accès réservé à l’équipe Easy Pièces." : "Accédez à votre espace client.")
    : mode === "register"
    ? "Rejoignez Easy Pièces."
    : "Entrez votre email pour recevoir un lien de réinitialisation.";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={() => setAuthOpen(false)}
      data-testid="auth-modal"
    >
      <div
        ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="auth-title"
        className="bg-white border-2 border-black shadow-hard-red w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setAuthOpen(false)}
          className="absolute top-4 right-4 hover:text-[#E60000]"
          aria-label="Fermer"
          data-testid="auth-close-btn"
        >
          <X size={22} weight="bold" />
        </button>
        <h2 id="auth-title" className="font-display font-black text-3xl mb-1">{title}</h2>
        <p className="text-zinc-500 text-sm mb-6">{subtitle}</p>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <>
              <input
                className="input-base" placeholder="Nom complet"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="auth-name-input"
              />
              <input
                className="input-base" placeholder="Téléphone (optionnel)"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="auth-phone-input"
              />
            </>
          )}
          <input
            className="input-base" type="email" placeholder="Email" aria-label="Email de connexion" autoComplete="username"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            data-testid="auth-email-input"
          />
          {mode !== "forgot" && (
            <input
              className="input-base" type="password" placeholder="Mot de passe" aria-label="Mot de passe" autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              required minLength={6}
              data-testid="auth-password-input"
            />
          )}
          {mode === "login" && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="text-sm text-zinc-500 hover:text-[#E60000] underline"
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}
          <button
            type="submit" className="btn-primary w-full" disabled={loading}
            data-testid="auth-submit-btn"
          >
            {loading ? "..." : mode === "login" ? "Se connecter" : mode === "register" ? "S'inscrire" : "Envoyer le lien"}
          </button>
        </form>

        {(!isTeam || mode !== "login") && <div className="mt-4 text-center text-sm text-zinc-600">
          {mode === "login" ? (
            <>
              Pas de compte ?{" "}
              <button onClick={() => setMode("register")} className="text-[#E60000] font-bold underline" data-testid="switch-to-register">
                Créer un compte
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setMode("login")} className="text-[#E60000] font-bold underline" data-testid="switch-to-login">
                ← Retour à la connexion
              </button>
            </>
          )}
        </div>}
      </div>
    </div>
  );
}
